import React, { useEffect, useState } from 'react';
import { getCustomersData, getItemsData, getSection } from './saleReportHelpers';
import { API_BASE_URL } from '../../../../config';

// เพิ่มการขายที่รู้ชัด — unlike รายการขาย's recode editor (which only
// changes an EXISTING sale's saleCode), this creates a brand-new sale
// row with every field known (real customer, product, qty AND price).
// Product picker reuses getItemsData(fullReport) like the tablet's
// item breakdown; customerId is free-text with a datalist covering
// ALL customers in the system (report customers, then this seller's
// regular customers, then everyone else), not just customers already
// on this report — Vector 2026-09-25: "ลูกค้าลืมเป็นลูกค้าทั้งหมดที่มี
// ไม่ใช่แค่ลูกค้าที่ขาย". A genuinely-new customer can still be typed
// in free-text. If the extra fetches fail, the datalist just falls
// back to report customers only.
const AddSaleTab = ({
    fullReport,
    addSaleCustomerId,
    setAddSaleCustomerId,
    addSaleSelectedIceCode,
    setAddSaleSelectedIceCode,
    addSaleQtyInput,
    setAddSaleQtyInput,
    addSalePriceInput,
    setAddSalePriceInput,
    addSalePendingItems,
    addSalePendingItem,
    removeAddSalePendingItem,
    handleAddSale,
    addingSale,
}) => {
    const master = getSection(fullReport, ['master', 'masterReport', 'report', 'masterData'], null);
    const isVerified = master?.verified === true;
    const sellerId = master?.sellerId;
    const { list: custList } = getCustomersData(fullReport);

    // Loaded once when the tab mounts: [routine customers (no name), ...by-category
    // customers (with name)], in that order so routine customers can borrow a name
    // from the by-category pass below without a second round trip.
    const [extraCustomers, setExtraCustomers] = useState([]);

    useEffect(() => {
        if (!sellerId) return;
        let cancelled = false;
        const userStr = localStorage.getItem('user');
        const token = userStr ? JSON.parse(userStr)?.token : null;
        if (!token) return;
        const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

        (async () => {
            const merged = [];
            try {
                const res = await fetch(`${API_BASE_URL}/customer/api/customer-buy-rutine/seller/${encodeURIComponent(sellerId)}`, { headers });
                if (res.ok) {
                    const data = await res.json();
                    (Array.isArray(data) ? data : []).forEach(c => {
                        if (c.customerId) merged.push({ customerId: c.customerId, name: null });
                    });
                }
            } catch (e) {
                console.error('AddSaleTab: fetch customer-buy-rutine error:', e);
            }
            try {
                // sellerId intentionally omitted — sales_territory is empty on prod, so
                // sending sellerId here comes back an empty list.
                const res = await fetch(`${API_BASE_URL}/customer/api/customers/by-category`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ type: 'ALL' }),
                });
                if (res.ok) {
                    const data = await res.json();
                    (data?.regular || []).forEach(c => {
                        if (c.customerUid) merged.push({ customerId: c.customerUid, name: c.storeName || c.customerName || null });
                    });
                    (data?.walkIn || []).forEach(c => {
                        if (c.walkInCustomerUid) merged.push({ customerId: c.walkInCustomerUid, name: c.walkInCustomerName || null });
                    });
                    (data?.agent || []).forEach(c => {
                        if (c.agentCustomerUid) merged.push({ customerId: c.agentCustomerUid, name: c.agentName || c.storeName || null });
                    });
                    (data?.potential || []).forEach(c => {
                        if (c.potentialCustomerUid) merged.push({ customerId: c.potentialCustomerUid, name: c.potentialCustomerName || c.storeName || c.customerName || null });
                    });
                }
            } catch (e) {
                console.error('AddSaleTab: fetch customers/by-category error:', e);
            }
            if (!cancelled) setExtraCustomers(merged);
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sellerId]);

    const nameById = new Map();
    extraCustomers.forEach(c => { if (c.name) nameById.set(c.customerId, c.name); });

    const customerOptions = [];
    const seenCustomerIds = new Set();
    const addCustomerOption = (customerId, name) => {
        if (!customerId || seenCustomerIds.has(customerId)) return;
        seenCustomerIds.add(customerId);
        customerOptions.push({ customerId, name: name || nameById.get(customerId) || customerId });
    };
    custList.forEach(c => addCustomerOption(c.customerId, c.customerName || c.shopName));
    extraCustomers.forEach(c => addCustomerOption(c.customerId, c.name));

    const { items: rawItems } = getItemsData(fullReport);
    const productOptionsMap = new Map();
    rawItems.forEach(it => { if (it.iceCode && !productOptionsMap.has(it.iceCode)) productOptionsMap.set(it.iceCode, it); });
    const productOptions = Array.from(productOptionsMap.values());

    return (
        <div className="btc-section" style={{ gridColumn: 'span 2' }}>
            <h3 style={{ margin: '0 0 8px 0' }}>เพิ่มการขายที่รู้ชัด</h3>
            {isVerified ? (
                <p style={{ color: '#6c757d' }}>รายงานนี้ตรวจสอบแล้ว ไม่สามารถเพิ่มรายการขายได้</p>
            ) : (
                <>
                    <div style={{ marginBottom: '8px' }}>
                        <input
                            type="text"
                            list="add-sale-customer-list"
                            value={addSaleCustomerId}
                            onChange={e => setAddSaleCustomerId(e.target.value)}
                            placeholder="รหัสลูกค้า (customerId)"
                            style={{ width: '260px', padding: '5px 8px', borderRadius: '4px', border: '1px solid #ced4da' }}
                        />
                        <datalist id="add-sale-customer-list">
                            {customerOptions.map(c => (
                                <option key={c.customerId} value={c.customerId}>{c.name}</option>
                            ))}
                        </datalist>
                    </div>
                    {addSalePendingItems.length > 0 && (
                        <ul style={{ margin: '0 0 8px 0', paddingLeft: '18px' }}>
                            {addSalePendingItems.map((item, i) => (
                                <li key={i} style={{ fontSize: '13px' }}>
                                    {item.productName} x {item.quantity} @ {item.unitPrice.toLocaleString()} ฿ = {(item.quantity * item.unitPrice).toLocaleString()} ฿
                                    {' '}
                                    <button
                                        className="btc-btn btn-sm"
                                        style={{ backgroundColor: '#dc3545', padding: '1px 6px', fontSize: '11px' }}
                                        onClick={() => removeAddSalePendingItem(i)}
                                    >
                                        ลบ
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {productOptions.length === 0 ? (
                        <p style={{ color: '#6c757d' }}>ยังไม่มีรายการสินค้าให้เลือก — ต้องมีรายการขายอย่างน้อย 1 รายการในรายงานนี้ก่อน</p>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <select
                                value={addSaleSelectedIceCode}
                                onChange={e => setAddSaleSelectedIceCode(e.target.value)}
                                style={{ padding: '5px 8px', borderRadius: '4px', border: '1px solid #ced4da' }}
                            >
                                <option value="">เลือกสินค้า</option>
                                {productOptions.map(p => (
                                    <option key={p.iceCode} value={p.iceCode}>{p.productName}</option>
                                ))}
                            </select>
                            <input
                                type="number"
                                min="1"
                                value={addSaleQtyInput}
                                onChange={e => setAddSaleQtyInput(e.target.value.replace(/[^0-9]/g, ''))}
                                placeholder="จำนวน"
                                style={{ width: '90px', padding: '5px 8px', borderRadius: '4px', border: '1px solid #ced4da' }}
                            />
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={addSalePriceInput}
                                onChange={e => setAddSalePriceInput(e.target.value)}
                                placeholder="ราคา/หน่วย"
                                style={{ width: '110px', padding: '5px 8px', borderRadius: '4px', border: '1px solid #ced4da' }}
                            />
                            <button
                                className="btc-btn btn-sm"
                                style={{ backgroundColor: '#6c757d' }}
                                onClick={() => addSalePendingItem(productOptions)}
                            >
                                + เพิ่มรายการ
                            </button>
                            {addSalePendingItems.length > 0 && (
                                <button
                                    className="btc-btn btn-sm"
                                    style={{ backgroundColor: '#343a40' }}
                                    onClick={handleAddSale}
                                    disabled={addingSale}
                                >
                                    {addingSale ? 'กำลังบันทึก...' : `บันทึกทั้งหมด (${addSalePendingItems.length} รายการ)`}
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AddSaleTab;
